import axios from "axios";
import { Capacitor } from "@capacitor/core";
import { PushNotifications } from "@capacitor/push-notifications";
import { initializeApp } from "firebase/app";
import {
  deleteToken,
  getMessaging,
  getToken,
  isSupported,
  onMessage,
} from "firebase/messaging";
import { url } from "./url";

const firebaseConfig = {
  apiKey: "AIzaSyBiRLaeZ5Q5HU42ZeOy0gA_Q8AFOhm7KKI",
  authDomain: "marankoo.firebaseapp.com",
  projectId: "marankoo",
  storageBucket: "marankoo.firebasestorage.app",
  messagingSenderId: "848954995043",
  appId: "1:848954995043:web:ebccc3e3783cb70ec72356",
  measurementId: "G-BE7DSCVBZV"
};

const VAPID_KEY =
  "BPdB31VGGPIunYCJfGQf9PK95x_3-v2H3kTX6gclVfWlhv12ZHJiwomg-87sZ3xk0d3en8B5GCRnV2Jn-Chximc";
const DEVICE_ID_STORAGE_KEY = "marankoo_driver_device_id";
// A native APK must not call register() until its matching google-services.json
// has been installed and processed by the Android build.
const NATIVE_PUSH_ENABLED = process.env.REACT_APP_NATIVE_PUSH_ENABLED === "true";

const app = initializeApp(firebaseConfig);

let messagingPromise;
let serviceWorkerRegistrationPromise;
let lastSyncedToken = "";
let lastSyncedAccessToken = "";
let nativePushListenersPromise;
let nativeRegisterPromise;
let lastNativeToken = "";

function getAccessToken() {
  try {
    return localStorage.getItem("token") || "";
  } catch (error) {
    return "";
  }
}

function createDeviceId() {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  }

  if (typeof crypto !== "undefined" && typeof crypto.getRandomValues === "function") {
    const bytes = new Uint8Array(16);
    crypto.getRandomValues(bytes);
    return Array.from(bytes, (byte) => byte.toString(16).padStart(2, "0")).join("");
  }

  return `driver-${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

function getDriverDeviceId() {
  try {
    let deviceId = localStorage.getItem(DEVICE_ID_STORAGE_KEY);
    if (!deviceId) {
      deviceId = createDeviceId();
      localStorage.setItem(DEVICE_ID_STORAGE_KEY, deviceId);
    }
    return deviceId;
  } catch (error) {
    return createDeviceId();
  }
}

async function getMessagingInstance() {
  if (!messagingPromise) {
    messagingPromise = isSupported()
      .then((supported) => (supported ? getMessaging(app) : null))
      .catch((error) => {
        console.warn("Firebase messaging is not supported in this browser.", error);
        return null;
      });
  }

  return messagingPromise;
}

async function getMessagingServiceWorkerRegistration() {
  if (typeof navigator === "undefined" || !("serviceWorker" in navigator)) {
    return null;
  }

  if (!serviceWorkerRegistrationPromise) {
    serviceWorkerRegistrationPromise = navigator.serviceWorker
      .register("/firebase-messaging-sw.js")
      .catch((error) => {
        serviceWorkerRegistrationPromise = undefined;
        console.warn("Firebase messaging service worker registration failed.", error);
        return null;
      });
  }

  return serviceWorkerRegistrationPromise;
}

async function savePushToken(pushToken, accessToken) {
  if (!pushToken || !accessToken) {
    return;
  }

  if (
    pushToken === lastSyncedToken &&
    accessToken === lastSyncedAccessToken
  ) {
    return;
  }

  await axios.post(
    `${url}/fcm/v1/save-push-token-driver`,
    {
      push_token: pushToken,
      device_id: getDriverDeviceId(),
    },
    {
      headers: {
        Authorization: `Bearer ${accessToken}`,
      },
    }
  );

  lastSyncedToken = pushToken;
  lastSyncedAccessToken = accessToken;
}

async function ensureNativePushListeners() {
  if (!nativePushListenersPromise) {
    nativePushListenersPromise = (async () => {
      await PushNotifications.addListener("registration", ({ value }) => {
        lastNativeToken = value;
        savePushToken(value, getAccessToken()).catch((error) => {
          console.warn("Unable to sync native driver FCM token.", error);
        });
      });
      await PushNotifications.addListener("registrationError", (error) => {
        console.warn("Native driver FCM registration failed.", error);
      });
    })().catch((error) => {
      nativePushListenersPromise = undefined;
      throw error;
    });
  }
  return nativePushListenersPromise;
}

// Permission belongs to Android, independently of whether Firebase registration
// was enabled when this APK was built. Do not hide the permission UI just because
// google-services.json / the build flag has not been configured yet.
function isNativePushConfigured() {
  return NATIVE_PUSH_ENABLED;
}

async function getNativePushPermission() {
  if (!Capacitor.isNativePlatform()) return "unavailable";
  try {
    const status = await PushNotifications.checkPermissions();
    return status.receive;
  } catch (error) {
    console.warn("Unable to check driver push permission.", error);
    return "unavailable";
  }
}

// May be invoked by the explicit Android permission button even when FCM is not
// configured yet. Registration and backend token syncing remain separately gated.
async function requestNativePushPermission() {
  if (!Capacitor.isNativePlatform()) return "unavailable";
  try {
    let permission = await PushNotifications.checkPermissions();
    if (["prompt", "prompt-with-rationale"].includes(permission.receive)) {
      permission = await PushNotifications.requestPermissions();
    }
    return permission.receive;
  } catch (error) {
    console.warn("Unable to request driver push permission.", error);
    return "unavailable";
  }
}

async function requestForNativeToken({ requestPermission }) {
  if (!NATIVE_PUSH_ENABLED || !getAccessToken()) {
    return "";
  }

  try {
    const permission = requestPermission
      ? await requestNativePushPermission()
      : await getNativePushPermission();
    if (permission !== "granted") {
      return "";
    }

    await ensureNativePushListeners();
    // register() emits its token asynchronously in the registration listener.
    // Never call it for an APK built without a matching Firebase Android config.
    if (!nativeRegisterPromise) {
      nativeRegisterPromise = PushNotifications.register().catch((error) => {
        nativeRegisterPromise = undefined;
        throw error;
      });
    }
    await nativeRegisterPromise;
    if (lastNativeToken) {
      await savePushToken(lastNativeToken, getAccessToken());
    }
    return lastNativeToken;
  } catch (error) {
    console.warn("Unable to register native driver push notifications.", error);
    return "";
  }
}

async function requestForToken({ requestPermission = true } = {}) {
  if (Capacitor.isNativePlatform()) {
    return requestForNativeToken({ requestPermission });
  }
  const accessToken = getAccessToken();
  if (!accessToken || typeof Notification === "undefined") {
    return "";
  }

  try {
    const messaging = await getMessagingInstance();
    if (!messaging) {
      return "";
    }

    let permission = Notification.permission;
    if (permission === "default" && requestPermission) {
      permission = await Notification.requestPermission();
    }

    if (permission !== "granted") {
      return "";
    }

    const serviceWorkerRegistration =
      await getMessagingServiceWorkerRegistration();
    if (!serviceWorkerRegistration) {
      return "";
    }

    const pushToken = await getToken(messaging, {
      vapidKey: VAPID_KEY,
      serviceWorkerRegistration,
    });

    if (!pushToken) {
      return "";
    }

    await savePushToken(pushToken, accessToken);
    return pushToken;
  } catch (error) {
    console.warn("Unable to register this device for push notifications.", error);
    return "";
  }
}

async function showForegroundNotification(payload) {
  const notificationPayload = payload && payload.notification;
  if (
    !notificationPayload ||
    typeof Notification === "undefined" ||
    Notification.permission !== "granted"
  ) {
    return;
  }

  const registration = await getMessagingServiceWorkerRegistration();
  if (!registration) {
    return;
  }

  await registration.showNotification(
    notificationPayload.title || "مارانکو رانندگان",
    {
      body: notificationPayload.body || "",
      icon: notificationPayload.icon || "/logo192.png",
      badge: "/logo192.png",
      data: payload.data || {},
    }
  );
}

async function listenForForegroundMessages(callback) {
  if (Capacitor.isNativePlatform()) {
    if (!NATIVE_PUSH_ENABLED) return () => {};
    const handle = await PushNotifications.addListener(
      "pushNotificationReceived",
      (notification) => {
        if (typeof callback === "function") {
          callback({
            notification: { title: notification.title, body: notification.body },
            data: notification.data || {},
          });
        }
      }
    );
    return () => { void handle.remove(); };
  }
  const messaging = await getMessagingInstance();
  if (!messaging) {
    return () => {};
  }

  return onMessage(messaging, (payload) => {
    showForegroundNotification(payload).catch((error) => {
      console.warn("Unable to show foreground notification.", error);
    });

    if (typeof callback === "function") {
      callback(payload);
    }
  });
}

async function releasePushToken() {
  const accessToken = getAccessToken();
  const deviceId = getDriverDeviceId();

  const serverLogout = accessToken
    ? axios
        .post(
          `${url}/driver/v1/logout`,
          { device_id: deviceId },
          {
            headers: {
              Authorization: `Bearer ${accessToken}`,
            },
            timeout: 5000,
          }
        )
        .catch((error) => {
          console.warn("Unable to remove push token from the server.", error);
        })
    : Promise.resolve();

  const localTokenRemoval = Capacitor.isNativePlatform()
    ? (NATIVE_PUSH_ENABLED
        ? PushNotifications.unregister().catch((error) => {
            console.warn("Unable to unregister native push token.", error);
          })
        : Promise.resolve())
    : getMessagingInstance()
        .then((messaging) => (messaging ? deleteToken(messaging) : false))
        .catch((error) => {
          console.warn("Unable to delete the local FCM token.", error);
          return false;
        });

  await Promise.all([serverLogout, localTokenRemoval]);
  lastSyncedToken = "";
  lastSyncedAccessToken = "";
  lastNativeToken = "";
  nativeRegisterPromise = undefined;
}

function clearLocalStoragePreservingDeviceId() {
  const deviceId = getDriverDeviceId();
  localStorage.clear();
  localStorage.setItem(DEVICE_ID_STORAGE_KEY, deviceId);
}

const onMessageListener = () =>
  new Promise((resolve) => {
    let unsubscribe = () => {};

    listenForForegroundMessages((payload) => {
      unsubscribe();
      resolve(payload);
    }).then((listenerUnsubscribe) => {
      unsubscribe = listenerUnsubscribe;
    });
  });

export {
  clearLocalStoragePreservingDeviceId,
  getDriverDeviceId,
  getNativePushPermission,
  isNativePushConfigured,
  requestNativePushPermission,
  listenForForegroundMessages,
  onMessageListener,
  releasePushToken,
  requestForToken,
};
